import { useQuery } from '@tanstack/react-query';
import { adminService } from '@/services/admin.service';
import { ArrowLeft, Database, FileText, TrendingUp, Target, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

export function AnalyticsRAG() {
  const { data: analytics, isLoading } = useQuery({
    queryKey: ['analytics', '30d'],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        return await adminService.getPipelinePerformance(token);
      } catch (error) {
        console.error('Failed to fetch RAG analytics:', error);
        return null;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const { rag_metrics } = analytics;
  const { retrieval_stats, chunk_usage, effectiveness, retrieval_performance } = rag_metrics;

  // Calculate additional metrics
  const totalQuestions = effectiveness.questions_with_rag + effectiveness.questions_without_rag;
  const ragAdoptionRate = (effectiveness.questions_with_rag / totalQuestions) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/analytics"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">RAG Performance</h1>
            <p className="text-gray-600">
              Retrieval-Augmented Generation metrics and effectiveness
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent">
            <option>Last 30 days</option>
            <option>Last 7 days</option>
            <option>Last 90 days</option>
            <option>All time</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Retrievals</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {retrieval_stats.total_retrievals.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {retrieval_stats.avg_chunks_per_retrieval.toFixed(1)} chunks avg
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Relevance Score</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(retrieval_stats.avg_relevance_score * 100).toFixed(1)}%
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {retrieval_stats.high_relevance_percent.toFixed(1)}% high relevance
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Quality Improvement</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                +{effectiveness.rag_quality_improvement.toFixed(1)}%
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">vs non-RAG questions</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Context Accuracy</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {effectiveness.context_accuracy_score.toFixed(1)}%
              </p>
            </div>
            <div className="p-3 bg-fk-orange-vibrant100 rounded-lg">
              <Zap className="w-6 h-6 text-#ea7500" />
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">Accurate retrievals</p>
        </div>
      </div>

      {/* RAG Effectiveness Banner */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              RAG Effectiveness Impact
            </h3>
            <p className="text-green-700 mb-4">
              RAG is improving question quality by {effectiveness.rag_quality_improvement.toFixed(1)}% and
              saving ${analytics.llm_costs.cost_savings.rag_optimization_savings.toFixed(2)} in LLM costs
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-green-600 font-medium">With RAG</p>
                <p className="text-2xl font-bold text-green-900">
                  {effectiveness.questions_with_rag.toLocaleString()}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {ragAdoptionRate.toFixed(1)}% adoption
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Without RAG</p>
                <p className="text-2xl font-bold text-green-900">
                  {effectiveness.questions_without_rag.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Quality Boost</p>
                <p className="text-2xl font-bold text-green-900">
                  +{effectiveness.rag_quality_improvement.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Cost Savings</p>
                <p className="text-2xl font-bold text-green-900">
                  ${analytics.llm_costs.cost_savings.rag_optimization_savings.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Retrieval Quality Distribution & Chunk Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Retrieval Quality Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Retrieval Quality Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={retrieval_performance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="relevance_range" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#10B981" name="Retrievals" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {retrieval_performance.map((item: any, index: number) => (
              <div key={item.relevance_range} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  ></div>
                  <span className="text-sm text-gray-700">{item.relevance_range}</span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                  {item.count.toLocaleString()} ({item.percent.toFixed(1)}%)
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chunk Usage Stats */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Chunk Usage Statistics</h3>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Total Chunks Indexed</span>
                <span className="text-lg font-bold text-gray-900">
                  {chunk_usage.total_chunks_indexed.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Chunks Used in Generation
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {chunk_usage.chunks_used_in_generation.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${chunk_usage.utilization_rate}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {chunk_usage.utilization_rate.toFixed(1)}% utilization rate
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">
                  Average Chunk Size
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {chunk_usage.avg_chunk_size_tokens} tokens
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Avg Chunks per Retrieval
                </span>
                <span className="text-lg font-bold text-gray-900">
                  {retrieval_stats.avg_chunks_per_retrieval.toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Retrieval Statistics */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Retrieval Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm text-gray-600 mb-1">Total Retrievals</p>
            <p className="text-2xl font-bold text-gray-900">
              {retrieval_stats.total_retrievals.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1">API calls to vector DB</p>
          </div>

          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm text-gray-600 mb-1">Avg Relevance Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {retrieval_stats.avg_relevance_score.toFixed(3)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Out of 1.000</p>
          </div>

          <div className="border-l-4 border-#f09628 pl-4">
            <p className="text-sm text-gray-600 mb-1">High Relevance Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {retrieval_stats.high_relevance_percent.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Score &gt; 0.8</p>
          </div>

          <div className="border-l-4 border-orange-500 pl-4">
            <p className="text-sm text-gray-600 mb-1">Avg Chunks Retrieved</p>
            <p className="text-2xl font-bold text-gray-900">
              {retrieval_stats.avg_chunks_per_retrieval.toFixed(1)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Per query</p>
          </div>
        </div>
      </div>

      {/* Effectiveness Metrics */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">RAG vs Non-RAG Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  With RAG
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Without RAG
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Improvement
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Questions Generated
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {effectiveness.questions_with_rag.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {effectiveness.questions_without_rag.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                  {ragAdoptionRate.toFixed(1)}% adoption
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Quality Score
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {(effectiveness.context_accuracy_score).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {(effectiveness.context_accuracy_score - effectiveness.rag_quality_improvement).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                  +{effectiveness.rag_quality_improvement.toFixed(1)}%
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Context Accuracy
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {effectiveness.context_accuracy_score.toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  N/A
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                  High accuracy
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Cost Efficiency
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  Optimized context
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  Full material scan
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                  ${analytics.llm_costs.cost_savings.rag_optimization_savings.toFixed(2)} saved
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          <FileText className="w-5 h-5 inline mr-2" />
          RAG Performance Insights
        </h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Your RAG system is performing excellently with an {retrieval_stats.avg_relevance_score.toFixed(1)} average relevance score
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              {retrieval_stats.high_relevance_percent.toFixed(1)}% of retrievals have high relevance (score &gt; 0.8), indicating well-chunked content
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Chunk utilization rate of {chunk_usage.utilization_rate.toFixed(1)}% shows balanced indexing
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              RAG is saving ${analytics.llm_costs.cost_savings.rag_optimization_savings.toFixed(2)}/month by providing targeted context instead of full material scans
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
